import React, { useState, useEffect, useCallback } from 'react';
import Layout from './Layout';
import PageHeader from './PageHeader';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { fetchDailyAnalysis } from '../api';

const DailySalesAnalysis = () => {
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('today');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalProfit: 0,
    netProfit: 0,
    avgDailySales: 0,
    totalOrders: 0,
    totalCashReceived: 0,
    totalDue: 0,
    totalStockValue: 0,
    totalStockQuantity: 0,
    totalCashInflow: 0,
    totalCashOutflow: 0,
    netCashFlow: 0
  });
  const [editDialog, setEditDialog] = useState({ open: false, data: null, isNew: false });
  const [cashBreakdown, setCashBreakdown] = useState({
    notes: {
      1000: 0,
      500: 0,
      200: 0,
      100: 0,
      50: 0,
      20: 0,
      10: 0,
      5: 0,
      2: 0
    },
    coins: {
      5: 0,
      2: 0,
      1: 0
    },
    digital: {
      bkash: 0
    }
  });

  const getDateRange = useCallback(() => {
    const today = new Date();
    switch (timeRange) {
      case 'today':
        return { startDate: format(today, 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
      case 'last7days':
        return {
          startDate: format(subDays(today, 7), 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd')
        };
      case 'last30days':
        return {
          startDate: format(subDays(today, 30), 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd')
        };
      case 'thisMonth':
        return {
          startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
          endDate: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      default:
        return { startDate: null, endDate: null };
    }
  }, [timeRange]);

  const loadDailyData = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const { startDate, endDate } = getDateRange();
      const response = await fetchDailyAnalysis(startDate, endDate, page);

      // The DailyAnalysisAPIView returns paginated data
      if (response.data && response.data.results) {
        const data = response.data.results;
        
        // Update pagination info
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / 10)); // Assuming 10 items per page
        setCurrentPage(page);
        
        setSummaryData({
          totalSales: data.summary.total_sales || 0,
          totalExpenses: data.summary.total_expenses || 0,
          totalProfit: data.summary.total_profit || 0,
          netProfit: data.summary.net_profit || 0,
          avgDailySales: data.summary.avg_daily_sales || 0,
          totalOrders: data.summary.total_orders || 0,
          totalCashReceived: data.summary.total_cash_received || 0,
          totalDue: data.summary.total_due || 0,
          totalStockValue: data.daily_data?.length > 0 ? data.daily_data[data.daily_data.length - 1].stock_value || 0 : 0,
          totalStockQuantity: data.daily_data?.length > 0 ? data.daily_data[data.daily_data.length - 1].stock_quantity || 0 : 0,
          totalCashInflow: data.summary.total_cash_inflow || 0,
          totalCashOutflow: data.summary.total_cash_outflow || 0,
          netCashFlow: data.summary.net_cash_flow || 0
        });

        setDailyData(
          data.daily_data.map((item) => ({
            date: item.date,
            dailySales: item.daily_sales || 0,
            dailyExpenses: item.daily_expenses || 0,
            dailyProfit: item.daily_profit || 0,
            netProfit: item.net_profit || 0,
            totalOrders: item.total_orders || 0,
            cashReceived: item.cash_received || 0,
            dueAmount: item.due_amount || 0,
            stockValue: item.stock_value || 0,
            stockQuantity: item.stock_quantity || 0,
            openingCash: item.opening_cash || 0,
            closingCash: item.closing_cash || 0,
            cashInflow: item.cash_inflow || 0,
            cashOutflow: item.cash_outflow || 0,
            netCashFlow: item.net_cash_flow || 0,
            // Additional fields for Excel-like functionality
            newInvestment: 0,
            previousWithdrawal: 0,
            purchaseExpenses: 0,
            totalExpenses: item.daily_expenses || 0,
            creditGiven: 0,
            creditReceived: 0,
            stock: item.stock_value || 0,
            backlogCash: 0,
            dailyCash: item.cash_received || 0,
            totalCapital: item.stock_value || 0
          }))
        );
      } else {
        setError('No data available for the selected date range');
        setDailyData([]);
        setSummaryData({
          totalSales: 0,
          totalExpenses: 0,
          totalProfit: 0,
          netProfit: 0,
          avgDailySales: 0,
          totalOrders: 0,
          totalCashReceived: 0,
          totalDue: 0,
          totalStockValue: 0,
          totalStockQuantity: 0,
          totalCashInflow: 0,
          totalCashOutflow: 0,
          netCashFlow: 0
        });
      }
    } catch (err) {
      setError('Failed to load daily analysis data');
      console.error('Error loading daily analysis:', err);
      setDailyData([]);
      setSummaryData({
        totalSales: 0,
        totalExpenses: 0,
        totalProfit: 0,
        netProfit: 0,
        avgDailySales: 0,
        totalOrders: 0,
        totalCashReceived: 0,
        totalDue: 0,
        totalStockValue: 0,
        totalStockQuantity: 0,
        totalCashInflow: 0,
        totalCashOutflow: 0,
        netCashFlow: 0
      });
    }
    setLoading(false);
  }, [getDateRange]); // Remove page dependency to avoid infinite loops

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when time range changes
    loadDailyData(1);
  }, [timeRange, loadDailyData]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
    setCurrentPage(1); // Reset to first page when time range changes
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    loadDailyData(newPage);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEdit = (item, isNew = false) => {
    setEditDialog({ open: true, data: item, isNew });
  };

  const handleSave = (editedData) => {
    if (editDialog.isNew) {
      const newData = [...dailyData, editedData];
      setDailyData(newData);
      loadDailyData(); // Refresh data from API
    } else {
      const updatedData = dailyData.map((item) =>
        item.date === editedData.date ? editedData : item
      );
      setDailyData(updatedData);
      loadDailyData(); // Refresh data from API
    }
    setEditDialog({ open: false, data: null, isNew: false });
  };

  const calculateCashTotal = () => {
    const notesTotal = Object.entries(cashBreakdown.notes).reduce(
      (sum, [denom, count]) => sum + parseInt(denom) * count,
      0
    );
    const coinsTotal = Object.entries(cashBreakdown.coins).reduce(
      (sum, [denom, count]) => sum + parseInt(denom) * count,
      0
    );
    const digitalTotal = Object.values(cashBreakdown.digital).reduce(
      (sum, amount) => sum + amount,
      0
    );
    return notesTotal + coinsTotal + digitalTotal;
  };

  return (
    <Layout>
      <PageHeader title="Daily Sales Analysis" subtitle="দৈনিক বিক্রয় বিশ্লেষণ" />

      <Box sx={{ p: 3 }}>
        {/* Time Range Filter */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={handleTimeRangeChange} label="Time Range">
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        {!loading && !error && (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="daily analysis tabs">
                <Tab label="Summary / সংক্ষিপ্ত তথ্য" />
                <Tab label="Daily Data / দৈনিক তথ্য" />
                <Tab label="Cash Breakdown / ক্যাশ বিভাজন" />
              </Tabs>
            </Box>

            {/* Tab 1: Summary Cards */}
            {tabValue === 0 && (
              <Grid container spacing={3}>
                {/* First row of summary cards */}
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        মোট বিক্রি / Total Sales
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalSales.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        মোট খরচ / Total Expenses
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalExpenses.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="success.main" gutterBottom>
                        মোট লাভ / Total Profit
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalProfit.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="info.main" gutterBottom>
                        গড় দৈনিক বিক্রয়
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.avgDailySales.toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Second row of summary cards */}
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="warning.main" gutterBottom>
                        মোট বাকি / Total Due
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalDue.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        স্টক মূল্য / Stock Value
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalStockValue.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        স্টক পরিমাণ / Stock Qty
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {summaryData.totalStockQuantity.toLocaleString()} pcs
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="success.main" gutterBottom>
                        মোট অর্ডার / Total Orders
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {summaryData.totalOrders.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Third row - Cash Flow Cards */}
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="success.main" gutterBottom>
                        নগদ আগমন / Cash Inflow
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalCashInflow?.toLocaleString() || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="error.main" gutterBottom>
                        নগদ বহির্গমন / Cash Outflow
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalCashOutflow?.toLocaleString() || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="info.main" gutterBottom>
                        নেট ক্যাশ ফ্লো / Net Cash Flow
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={summaryData.netCashFlow >= 0 ? 'success.main' : 'error.main'}>
                        ৳{summaryData.netCashFlow?.toLocaleString() || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        মোট নগদ / Total Cash Received
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ৳{summaryData.totalCashReceived?.toLocaleString() || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Tab 2: Daily Data Table */}
            {tabValue === 1 && (
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Daily Sales Data
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      size="small"
                      onClick={() =>
                        handleEdit(
                          {
                            date: format(new Date(), 'yyyy-MM-dd'),
                            dailySales: 0,
                            newInvestment: 0,
                            previousWithdrawal: 0,
                            purchaseExpenses: 0,
                            totalExpenses: 0,
                            dailyProfit: 0,
                            creditGiven: 0,
                            creditReceived: 0,
                            stock: 0,
                            backlogCash: 0,
                            dailyCash: 0,
                            totalCapital: 0
                          },
                          true
                        )
                      }>
                      Add Entry
                    </Button>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>তারিখ</TableCell>
                          <TableCell align="right">দৈনিক বিক্রয়</TableCell>
                          <TableCell align="right">মোট খরচ</TableCell>
                          <TableCell align="right">দৈনিক লাভ</TableCell>
                          <TableCell align="right">দৈনিক বাকি</TableCell>
                          <TableCell align="right">স্টক মূল্য</TableCell>
                          <TableCell align="right">শুরুর ক্যাশ</TableCell>
                          <TableCell align="right">শেষের ক্যাশ</TableCell>
                          <TableCell align="right">নেট ক্যাশ ফ্লো</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyData.map((row, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{format(new Date(row.date), 'dd-MM-yyyy')}</TableCell>
                            <TableCell align="right">৳{row.dailySales.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              ৳{row.totalExpenses.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">৳{row.dailyProfit.toLocaleString()}</TableCell>
                            <TableCell align="right">৳{row.dueAmount.toLocaleString()}</TableCell>
                            <TableCell align="right">৳{row.stockValue.toLocaleString()}</TableCell>
                            <TableCell align="right">৳{row.openingCash.toLocaleString()}</TableCell>
                            <TableCell align="right">৳{row.closingCash.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ color: row.netCashFlow >= 0 ? 'green' : 'red' }}>
                              ৳{row.netCashFlow.toLocaleString()}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => handleEdit(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Pagination */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="body2" align="center">
                        Showing {dailyData.length} of {totalCount} entries
                      </Typography>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                      />
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Tab 3: Cash Breakdown */}
            {tabValue === 2 && (
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cash On Hand Breakdown
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Notes (নোট)
                  </Typography>
                  {Object.entries(cashBreakdown.notes).map(([denom, count]) => (
                    <Box
                      key={denom}
                      sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">৳{denom}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={count}
                          onChange={(e) =>
                            setCashBreakdown((prev) => ({
                              ...prev,
                              notes: { ...prev.notes, [denom]: parseInt(e.target.value) || 0 }
                            }))
                          }
                          sx={{ width: 60, mr: 1 }}
                        />
                        <Typography variant="body2">
                          = ৳{(parseInt(denom) * count).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Coins (কয়েন)
                  </Typography>
                  {Object.entries(cashBreakdown.coins).map(([denom, count]) => (
                    <Box
                      key={denom}
                      sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">৳{denom}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={count}
                          onChange={(e) =>
                            setCashBreakdown((prev) => ({
                              ...prev,
                              coins: { ...prev.coins, [denom]: parseInt(e.target.value) || 0 }
                            }))
                          }
                          sx={{ width: 60, mr: 1 }}
                        />
                        <Typography variant="body2">
                          = ৳{(parseInt(denom) * count).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Digital (ডিজিটাল)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">bKash</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={cashBreakdown.digital.bkash}
                      onChange={(e) =>
                        setCashBreakdown((prev) => ({
                          ...prev,
                          digital: { ...prev.digital, bkash: parseInt(e.target.value) || 0 }
                        }))
                      }
                      sx={{ width: 100 }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography variant="h6">Total Cash:</Typography>
                    <Typography variant="h6" color="primary">
                      ৳{calculateCashTotal().toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, data: null, isNew: false })}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{editDialog.isNew ? 'Add New Entry' : 'Edit Entry'}</DialogTitle>
        <DialogContent>
          {editDialog.data && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={editDialog.data.date}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, date: e.target.value }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Daily Sales (দৈনিক বিক্রয়)"
                  type="number"
                  value={editDialog.data.dailySales}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, dailySales: parseInt(e.target.value) || 0 }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="New Investment (নতুন বিনিয়োগ)"
                  type="number"
                  value={editDialog.data.newInvestment}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, newInvestment: parseInt(e.target.value) || 0 }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Expenses (মোট খরচ)"
                  type="number"
                  value={editDialog.data.totalExpenses}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, totalExpenses: parseInt(e.target.value) || 0 }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Daily Profit (দৈনিক লাভ)"
                  type="number"
                  value={editDialog.data.dailyProfit}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, dailyProfit: parseInt(e.target.value) || 0 }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Daily Cash (দৈনিক হাতের ক্যাশ)"
                  type="number"
                  value={editDialog.data.dailyCash}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      data: { ...prev.data, dailyCash: parseInt(e.target.value) || 0 }
                    }))
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, data: null, isNew: false })}>
            Cancel
          </Button>
          <Button onClick={() => handleSave(editDialog.data)} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default DailySalesAnalysis;
